import { useCallback, useRef, useState } from 'react';
import { Box, Popover, Typography, Divider, CircularProgress } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useQuery } from '@tanstack/react-query';
import { usePermissions } from '@/hooks/use-permissions';
import { appVersion } from '@/lib/app-version';

const API_ROOT = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface HealthResponse {
  status?: string;
  version?: string;
  build_time?: string;
  git_commit?: string;
  uptime?: string;
}

function shortSha(s?: string): string {
  if (!s || s === 'unknown' || s === 'dev') return s || '—';
  return s.length > 12 ? s.slice(0, 12) : s;
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, fontSize: 12, lineHeight: 1.6 }}>
      <Typography component="span" sx={{ fontSize: 'inherit', color: 'text.secondary', minWidth: 78 }}>
        {label}
      </Typography>
      <Typography
        component="span"
        sx={{ fontSize: 'inherit', fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Fixed bottom-right widget (rendered inline on the system line). On hover it
 * surfaces UI + backend build info (version / commit / build time / uptime).
 * Super-admin only — returns null otherwise.
 */
export function BuildInfoWidget() {
  const { isSuperAdmin } = usePermissions();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimer = useRef<number | null>(null);
  const open = Boolean(anchorEl);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const openPop = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      cancelClose();
      setAnchorEl(e.currentTarget);
    },
    [cancelClose]
  );

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setAnchorEl(null), 180);
  }, [cancelClose]);

  const health = useQuery({
    queryKey: ['system', 'health'],
    queryFn: async (): Promise<HealthResponse> => {
      const res = await fetch(`${API_ROOT}/health`);
      return res.json();
    },
    enabled: open && isSuperAdmin,
    staleTime: 60_000,
    retry: false,
  });

  if (!isSuperAdmin) return null;

  // Already carries its own leading 'v'. Rendering v{uiVersion} here is what
  // produced 'vbeta-v1.1.0-a1b2c3d4' on beta.
  const uiVersion = appVersion();
  const uiCommit = shortSha(import.meta.env.VITE_GIT_COMMIT);
  const uiBuild = import.meta.env.VITE_BUILD_TIME || '—';

  return (
    <>
      <Box
        onMouseEnter={openPop}
        onMouseLeave={scheduleClose}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: 'inherit',
          color: 'text.disabled',
          cursor: 'help',
          pointerEvents: 'auto',
          opacity: 0.7,
          transition: 'opacity .15s ease',
          '&:hover': { opacity: 1 },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 13 }} />
        <Typography component="span" sx={{ fontSize: 'inherit', color: 'inherit' }}>
          {uiVersion}
        </Typography>
        <Box component="span" sx={{ opacity: 0.6, mx: 0.25 }}>·</Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        disableRestoreFocus
        sx={{ pointerEvents: 'none' }}
        slotProps={{
          paper: {
            onMouseEnter: cancelClose,
            onMouseLeave: scheduleClose,
            sx: { pointerEvents: 'auto', width: 300, maxWidth: '92vw', p: 1.5, mb: 0.5 },
          },
        }}
      >
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4 }}>
          BUILD &amp; SYSTEM INFO
        </Typography>

        <Box sx={{ mt: 1 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.25 }}>Frontend (UI)</Typography>
          <InfoRow label="version" value={uiVersion} />
          <InfoRow label="commit" value={uiCommit} />
          <InfoRow label="built" value={uiBuild} />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.25 }}>Backend (API)</Typography>
          {health.isLoading ? (
            <CircularProgress size={14} />
          ) : health.isError ? (
            <Typography sx={{ fontSize: 12, color: 'error.main' }}>unreachable</Typography>
          ) : (
            <>
              <InfoRow label="version" value={health.data?.version || '—'} />
              <InfoRow label="commit" value={shortSha(health.data?.git_commit)} />
              <InfoRow label="built" value={health.data?.build_time || '—'} />
              <InfoRow label="uptime" value={health.data?.uptime || '—'} />
              <InfoRow label="status" value={health.data?.status || '—'} />
            </>
          )}
        </Box>
      </Popover>
    </>
  );
}

export default BuildInfoWidget;
